import { formatDate, getLabelFromStorage, replaceStringPlaceholders } from '@pb/utils'
import { sortWith, descend, ascend, prop, map, compose } from 'ramda'

import {
  getActiveOfferExpiresAt,
  isInLocalStorage,
  getOfferIdFromLocalStorage,
  getOfferFromLocalStorage,
} from '../helpers/localStorage'
import { MergedOffers, DateInfo, ConfiguredOffers, Offer } from '../types'

const setIsOfferActive = (offer, customerLoyaltyId): any => {
  const LS = window.localStorage
  const isOfferActive = isInLocalStorage(LS, customerLoyaltyId, offer.offerPropositionId)
  const activeOfferExpiresAt = isOfferActive ? getActiveOfferExpiresAt(LS, customerLoyaltyId) : null

  return {
    isOfferActive,
    activeOfferExpiresAt,
    ...offer,
  }
}

const getDayDiff = (endDay: number): number => {
  const today = new Date()
  const diff = endDay - today.getTime()
  const seconds = diff / 1000
  const days = Math.floor(seconds / (3600 * 24))
  return days
}

export const voucherDateInfoText = (rewards: MergedOffers): DateInfo | null => {
  const {
    endDateTime,
    endDateTimeMs,
    isValidUntilEnable,
    endDateTimeFormatted,
    isValidFromEnable,
    startDateTimeFormatted,
    validFromLabel,
    benefitNearingExpiryThresholdDays,
    personalizedExpireDateFormatted,
  } = rewards
  const expiresInDays = endDateTime !== null ? getDayDiff(endDateTimeMs) : -1
  if (
    isValidUntilEnable &&
    endDateTime &&
    benefitNearingExpiryThresholdDays > 0 &&
    expiresInDays <= benefitNearingExpiryThresholdDays &&
    expiresInDays >= 0
  ) {
    return {
      infoText: replaceStringPlaceholders(
        getLabelFromStorage('account.rewards.expiration.message'),
        [expiresInDays],
      ),
      date: endDateTimeFormatted,
      isExpired: true,
    }
  }
  if (isValidFromEnable && !isValidUntilEnable && startDateTimeFormatted) {
    return {
      infoText: `${validFromLabel} ${startDateTimeFormatted}`,
      date: startDateTimeFormatted,
      isExpired: false,
    }
  }
  if (isValidUntilEnable && personalizedExpireDateFormatted) {
    return {
      infoText: `${getLabelFromStorage(
        'account.rewards.validity.message',
      )} ${personalizedExpireDateFormatted}`,
      date: personalizedExpireDateFormatted,
      isExpired: false,
    }
  }
  return null
}

const formatDates = (allOffers): any => {
  const dateInfoText = voucherDateInfoText(allOffers)
  const { personalizedExpireDate, startDateTime, ...rest } = allOffers
  return {
    dateInfoText,
    personalizedExpireDateFormatted: personalizedExpireDate && formatDate(personalizedExpireDate),
    personalizedExpireDate,
    startDateTimeFormatted: startDateTime && formatDate(startDateTime),
    startDateTime,
    ...rest,
  }
}

/**
 * Merges properties from each offerProposition with properties from the matching configuredOffers and active offer in localStorage
 */
const mergeOffers = (configuredOffers, offerPropositions, customerLoyaltyId): any => {
  const { localStorage } = window
  const activeOfferId = getOfferIdFromLocalStorage(localStorage, customerLoyaltyId)
  const activeOffer = activeOfferId && getOfferFromLocalStorage(localStorage, customerLoyaltyId)
  const matchingActiveOffer = offerPropositions.find(
    (proposition) => proposition.offerPropositionId === activeOfferId,
  )

  return [!matchingActiveOffer && { ...activeOffer }, ...offerPropositions].reduce(
    (offers, proposition) => {
      const matchingOffer = configuredOffers.find((offer) => offer.key === proposition.offerKey)

      return matchingOffer
        ? [
            ...offers,
            {
              ...matchingOffer,
              ...proposition,
            },
          ]
        : offers
    },
    [],
  )
}

/**
 * Merges, sorts, and sets active status
 */
export const normalizeMatchingOffers = (
  configuredOffers,
  offerPropositions,
  customerLoyaltyId,
): any => {
  const offers: any = mergeOffers(configuredOffers, offerPropositions, customerLoyaltyId)
  return compose(
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    sortWith([descend(prop('isOfferActive')), ascend(prop('activeOfferExpiresAt'))]),
    map((offer) => setIsOfferActive(offer, customerLoyaltyId)),
    map((offer: any) => formatDates(offer)),
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
  )(offers)
}

export const convertOfferDetailData = (rewardDataArray): any => {
  if (!rewardDataArray?.length) return null

  const offer = rewardDataArray.reduce((acc, data) => {
    const { nodeName, ...restProps } = data
    if (nodeName) {
      delete restProps['sling:resourceType']
      acc[nodeName] = { ...restProps }
      if (nodeName === 'redeembutton') {
        const { addtoshoppingbagbutton } = acc[nodeName]
        acc[nodeName].addToShoppingBagText = addtoshoppingbagbutton || null
        delete acc[nodeName].addtoshoppingbagbutton
      }
    } else {
      const resourceTypePath = restProps['sling:resourceType'].split('/')
      const key = resourceTypePath[resourceTypePath.length - 1]
      acc[key] = { ...restProps }
    }
    return acc
  }, {})

  offer.key = offer['jcr:content'].offerId

  return offer
}

export const getMyNonExpiredBonus = (rewards): MergedOffers[] => {
  const availableRewards = rewards.filter((reward) => {
    const now = new Date()

    // @ts-expect-error ts-migrate(2363) FIXME: The right-hand side of an arithmetic operation mus... Remove this comment to see the full error message
    return !(reward.isOfferActive && reward.activeOfferExpiresAt - now < 0)
  })
  return availableRewards?.sort(function (a, b) {
    return b.isOfferActive - a.isOfferActive || a.activeOfferExpiresAt - b.activeOfferExpiresAt
  })
}

export const mergeRewards = (
  memberOffers: ConfiguredOffers[],
  offersProposition: Offer[],
  customerLoyaltyId: number,
): MergedOffers[] => {
  const allRewards: MergedOffers[] = []

  offersProposition.forEach((offer) => {
    const matchedOffer = formatDates({
      ...memberOffers.find((item) => item.key === offer.offerKey),
      ...offer,
    })

    const updatedOffer = setIsOfferActive(matchedOffer, customerLoyaltyId)

    allRewards.push(updatedOffer)
  })
  return allRewards
}
